import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * BannerAd — house or AdSense unit rendered inline within a homepage
 * / Explore section.
 *
 * Design contract (Section B / Part 3):
 *   * Same black-bordered offset-drop-shadow card treatment as
 *     `components/public/CampaignCard.jsx`, so a banner reads as one
 *     more card in the section — never a foreign box.
 *   * CityDooh gold #F5A800 / black #0A0A0A / cream #F7F4EC only.
 *   * "Sponsored" eyebrow in the same muted small-caps used by
 *     section eyebrows like "Trending now" / "Simple pricing".
 *
 * Behaviour:
 *   * On mount, fetches the zone's active banners (server-side
 *     random shuffle, so `banners[0]` is our pick).
 *   * Impression fired once on first visible mount via
 *     IntersectionObserver — hidden-in-scroll banners don't over-count.
 *   * Click opens `target_url` in a new tab and fires the tracking
 *     endpoint in parallel (fire-and-forget, never blocks nav).
 *   * If the zone has AdSense config populated on global_pricing,
 *     the API returns an `adsense` block; we render the AdSense unit
 *     INSTEAD of a house banner (mutually exclusive per zone).
 *
 * Props:
 *   zone       — one of "homepage_top" | "between_offers" | "between_stores"
 *   className  — passthrough grid/positioning classes
 *   compact    — when true, uses the shorter card ratio suited for
 *                inline grid slots. Homepage_top uses the taller ratio.
 */
export function BannerAd({ zone, className = "", compact = false }) {
  const [payload, setPayload] = useState(null);
  const [imgError, setImgError] = useState(false);
  const seenRef = useRef(false);
  const rootRef = useRef(null);

  useEffect(() => {
    let alive = true;
    api.publicBannerAds(zone)
      .then((r) => alive && setPayload(r.data || null))
      .catch(() => alive && setPayload({ banners: [], adsense: null }));
    return () => { alive = false; };
  }, [zone]);

  const banner = useMemo(() => (payload?.banners || [])[0] || null, [payload]);
  const adsense = payload?.adsense || null;

  // IntersectionObserver: count one impression per unique visible mount.
  useEffect(() => {
    if (!banner || !rootRef.current || seenRef.current) return undefined;
    const el = rootRef.current;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !seenRef.current) {
          seenRef.current = true;
          api.bannerAdImpression(banner.id).catch(() => {});
          io.disconnect();
        }
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [banner]);

  const handleClick = (e) => {
    if (!banner) return;
    // Open in a new tab regardless of the default anchor behavior
    // so users don't lose their scroll position on the homepage.
    e.preventDefault();
    api.bannerAdClick(banner.id).catch(() => {});
    if (banner.target_url) {
      window.open(banner.target_url, "_blank", "noopener,noreferrer");
    }
  };

  // No banner AND no adsense → render nothing (never leave a hole).
  if (!banner && !adsense) return null;

  // AdSense wins over house banner when configured for the zone.
  if (adsense) {
    return (
      <div ref={rootRef} className={className} data-testid={`banner-adsense-${zone}`}>
        <div className="relative border-2 border-[#0A0A0A] shadow-[6px_6px_0_#0A0A0A] bg-[#F7F4EC] p-3">
          <div className="text-[9px] font-black uppercase tracking-[0.2em] text-[#0A0A0A]/60 mb-2">
            Sponsored
          </div>
          <ins
            className="adsbygoogle block w-full"
            style={{ display: "block", minHeight: compact ? 220 : 260 }}
            data-ad-client={adsense.client_id}
            data-ad-slot={adsense.slot_id}
            data-ad-format="auto"
            data-full-width-responsive="true"
          />
        </div>
      </div>
    );
  }

  return (
    <a
      ref={rootRef}
      href={banner.target_url || "#"}
      onClick={handleClick}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className={`group relative block overflow-hidden bg-[#F7F4EC] border-2 border-[#0A0A0A] shadow-[6px_6px_0_#0A0A0A] transition-transform hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F5A800] ${className}`}
      data-testid={`banner-ad-${zone}-${banner.id}`}
      aria-label={`${banner.sponsor_name || "Sponsored"} — ${banner.label || "Sponsored"}`}
    >
      {/* Sponsored eyebrow — same muted small-caps as section eyebrows */}
      <div className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 rounded-full bg-[#0A0A0A]/85 px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.2em] text-[#F7F4EC]">
        {(banner.label || "Sponsored").slice(0, 18)}
      </div>
      {imgError ? (
        <div
          className={`w-full flex items-center justify-center bg-[#F5A800]/10 ${compact ? "aspect-[16/10]" : "aspect-[16/7]"}`}
          style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
        >
          <span className="text-2xl md:text-4xl uppercase tracking-tight text-[#0A0A0A]/70">
            {banner.sponsor_name || "Featured"}
          </span>
        </div>
      ) : (
        <img
          src={banner.image_url}
          alt={banner.sponsor_name || "Sponsored banner"}
          onError={() => setImgError(true)}
          className={`w-full h-full ${compact ? "aspect-[16/10]" : "aspect-[16/7]"} object-cover`}
          loading="lazy"
        />
      )}
      {banner.sponsor_name && (
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between">
          <span
            className="rounded-md bg-[#F7F4EC] px-2 py-1 text-[10px] font-black uppercase tracking-widest text-[#0A0A0A] border-2 border-[#0A0A0A]"
            style={{ fontFamily: '"Anton", "Impact", sans-serif' }}
          >
            {banner.sponsor_name}
          </span>
        </div>
      )}
    </a>
  );
}

/**
 * insertBanners — helper for grid sections. Splices ONE banner card
 * into a list of real cards according to the Section B / Part 4
 * cadence rules:
 *   * Fewer than 6 cards → append one banner at the end (never
 *     "single-card then banner then nothing").
 *   * 6 or more → insert one banner every `every` cards (default 6).
 *
 * Returns an array of `{ kind: "card" | "banner", ... }` items so the
 * caller can render each in place with a stable key.
 */
export function insertBanners(cards, zone, { every = 6, minReal = 6 } = {}) {
  if (!Array.isArray(cards) || cards.length === 0) return [];
  const items = cards.map((c, i) => ({ kind: "card", key: `c-${c.id || i}`, data: c }));
  if (cards.length < minReal) {
    items.push({ kind: "banner", key: `b-${zone}-end`, zone });
    return items;
  }
  const out = [];
  cards.forEach((c, i) => {
    out.push({ kind: "card", key: `c-${c.id || i}`, data: c });
    // Insert AFTER every `every`-th card, but never as the very last item
    // (avoids trailing-banner right before section footer).
    if ((i + 1) % every === 0 && i < cards.length - 1) {
      out.push({ kind: "banner", key: `b-${zone}-${i}`, zone });
    }
  });
  return out;
}

export default BannerAd;
